import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../lib/api/client';
import { useBoardStore } from '../../../stores/boardStore';

/**
 * useDeepVerdict - Hook to trigger a Tier-2 (LLM) analysis.
 *
 * On success, merges the LLM result into the existing page-payload cache
 * instead of invalidating the query. This makes the LLM state session-only:
 * - The user sees the LLM verdict immediately after triggering
 * - Any subsequent page-payload refetch (data change, navigation) returns
 *   fresh heuristic from the backend, resetting the verdict to Tier-1
 * - No tokens are wasted on automatic LLM re-runs
 */
export const useDeepVerdict = (boardId: string, pageId: string) => {
  const queryClient = useQueryClient();
  const { dateRange } = useBoardStore();

  return useMutation({
    mutationFn: async () => {
      try {
        const response = await apiClient.post(`/boards/${boardId}/pages/${pageId}/verdict/analyze`);
        return response.data.data;
      } catch (err: any) {
        if (err.response?.status === 403) {
          throw err.response.data; // This will contain code, resetKey, resetHours
        }
        throw err;
      }
    },
    onSuccess: (data) => {
      // Merge LLM result into the cached page-payload (session-only).
      // The next refetch from the backend will overwrite this with fresh heuristic.
      const queryKey = ['page-payload', boardId, pageId, dateRange.startDate, dateRange.endDate];
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return old;
        const analysis = data.analysis || data;
        return {
          ...old,
          aiVerdictState: 'llm',
          aiVerdict: {
            ...(old.aiVerdict || {}),
            // Preserve heuristic for reference, overlay LLM data at top level
            title: analysis.title,
            message: analysis.message,
            recommendation: analysis.recommendation,
            details: analysis.details || [],
            metrics: analysis.metrics || [],
            llm: analysis.message, // Keep llm field for any downstream consumers
            lastAnalyzed: data.lastAnalyzed || new Date().toISOString(),
          },
        };
      });
    }
  });
};
